import {
	Box,
	Typography,
	Stack,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import { User, Loader, ChevronDown, ArrowRight } from "lucide-react";
import "../assignNurseModal.scss";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { OptionCategory, OptionRange } from "../../../interfaces/OptionCategory";
import variables from "../../../styles/variables.module.scss";
import { ReactComponent as ActividadesBasicas } from "../../../assets/icons/patients/TISS/Actividades básicas.svg";
import { ReactComponent as IntervencionesEspecificas } from "../../../assets/icons/patients/TISS/Intervenciones específicas.svg";
import { ReactComponent as SoporteCardiovascular } from "../../../assets/icons/patients/TISS/Soporte cardiovascular.svg";
import { ReactComponent as SoporteMetabolico } from "../../../assets/icons/patients/TISS/Soporte metabólico.svg";
import { ReactComponent as SoporteNeurologico } from "../../../assets/icons/patients/TISS/Soporte neurológico.svg";
import { ReactComponent as SoporteRenal } from "../../../assets/icons/patients/TISS/Soporte renal.svg";
import { ReactComponent as SoporteVentilatorio } from "../../../assets/icons/patients/TISS/Soporte ventilatorio.svg";
import { getHeaders, URI_API } from "../../../utils/config";
import { PatientsListContext } from "../../../components/PatientsList/PatientsList";
import { categoryReducer } from "../categoryReducer";

interface ModalTISSBodyProps {
	handleTab: () => void;
	setIsEdit: (value: boolean) => void;
	activeItems: any;
	categoryState: any;
	dispatchCategory: any;
}

export default function ModalTISSBody({
	handleTab,
	setIsEdit,
	activeItems,
	categoryState,
	dispatchCategory,
}: ModalTISSBodyProps) {
	const { selectedPatient, setSelectedPatient, setCategoryList } =
		useContext(PatientsListContext);
	const [isDisabled, setIsDisabled] = useState(true);
	const [tissInformation, setTissInformation] = useState<Map<string, OptionCategory[]>>();
	const [tissRange, setTissRanges] = useState<OptionRange[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const buttonRefs = useRef<Array<Array<HTMLButtonElement | null>>>([]);
	const accordionRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [activeAccordionIndex, setActiveAccordionIndex] = useState<number | null>(0);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

	//Lógica para obtener la info de la DB

	const getTISSInformation = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${URI_API}/patient_checklist_init/${selectedPatient?.id}?type=TISS`, 
                getHeaders()
            );
            const responseBody = await response.json();

            if (!response.ok) {
                console.error("Error del backend:", responseBody);
                setIsLoading(false);
                return;
            }

            const { 
                active_items = [], 
                ranges = [], 
                checklist = [] 
            } = responseBody;

            // 2. PROCESAR RANGOS
            // Nota: El backend ahora devuelve 'min' y 'max' directamente
            const formattedRanges = ranges.map((r: any) => ({
                id: r.id,
                description: r.description,
                category: r.category,
                min: parseFloat(r.min),
                max: parseFloat(r.max),
            }));
            setTissRanges(formattedRanges);

            // 3. PROCESAR CHECKLIST Y ITEMS ACTIVOS
            // Crear Set para búsqueda O(1)
            const activeIds = new Set(active_items.map((i: any) => i.id));

            const processedCategories = checklist.map((cat: any) => ({
                id: cat.id,
                category: cat.category,
                subcategory: cat.subcategory,
                description: cat.description,
                value: cat.value,
                checked: activeIds.has(cat.id), // Cruce de datos
            }));

            // Actualizar Contexto Global y Reducer Local
            setCategoryList(processedCategories);
            dispatchCategory({
                type: "INITIALIZE_OPTIONS",
                payload: {
                    categories: processedCategories.map((cat: any) => ({
                        id: cat.id,
                        value: cat.value,
                        checked: cat.checked,
                    })),
                },
            });

            // 4. CONSTRUIR MAPA PARA ACORDEÓN (Lógica Visual Original)
            let tiss_map = new Map<string, OptionCategory[]>();

            for (let tiss of processedCategories) {
                let option: OptionCategory = {
                    ...tiss,
                    title: "", // Se llenará en el switch
                };

                switch (option.subcategory) {
                    case "ventilatory_support":
                        option.title = "Soporte Ventilatorio";
                        break;
                    case "specific_interventions":
                        option.title = "Intervenciones Específicas";
                        break;
                    case "renal_support":
                        option.title = "Soporte Renal";
                        break;
                    case "neuro_support":
                        option.title = "Soporte Neurológico";
                        break;
                    case "metabol_support":
                        option.title = "Soporte Metabólico";
                        break;
                    case "cardio_support":
                        option.title = "Soporte Cardiovascular";
                        break;
                    case "basic_activities":
                        option.title = "Actividades Básicas";
                        break;
                }

                if (tiss_map.has(option.subcategory)) {
                    let currentOptions: OptionCategory[] = tiss_map.get(option.subcategory)!;
                    currentOptions.push(option);
                    tiss_map.set(option.subcategory, currentOptions);
                } else {
                    tiss_map.set(option.subcategory, [option]);
                }
            }

            setTissInformation(tiss_map);

            if (selectedPatient?.category_id === null) {
                setIsEdit(true);
            }

        } catch (error) {
            console.error("Error fetching TISS info:", error);
        } finally {
            setIsLoading(false);
        }
    };

	useEffect(() => {
		getTISSInformation();
	}, []);

	//Lógica para establecer características para renderizado

	const SubcategoryIcon = ({ subcategory }: { subcategory: string }) => {
		let icon = <Box></Box>;
		switch (subcategory) {
			case "ventilatory_support":
				icon = <SoporteVentilatorio className="icons-medium" />;
				break;
			case "specific_interventions":
				icon = <IntervencionesEspecificas className="icons-medium" />;
				break;
			case "renal_support":
				icon = <SoporteRenal className="icons-large" />;
				break;
			case "neuro_support":
				icon = <SoporteNeurologico className="icons-medium" />;
				break;
			case "metabol_support":
				icon = <SoporteMetabolico className="icons-small" />;
				break;
			case "cardio_support":
				icon = <SoporteCardiovascular className="icons-small" />;
				break;
			case "basic_activities":
				icon = <ActividadesBasicas className="icons-small" />;
				break;
		}
		return (
			<>
				<Box
					sx={{ maxWidth: "fit-content", maxHeight: "fit-content", lineHeight: "normal" }}
				>
					{icon}
				</Box>
			</>
		);
	};

	const SubcategoryClassName = ({ subcategory }: { subcategory: string }) => {
		let className = "";
		switch (subcategory) {
			case "ventilatory_support":
				className = "tiss-sections-purple-section";
				break;
			case "specific_interventions":
				className = "tiss-sections-light-blue-section";
				break;
			case "renal_support":
				className = "tiss-sections-rose-section";
				break;
			case "neuro_support":
				className = "tiss-sections-yellow-section";
				break;
			case "metabol_support":
				className = "tiss-sections-green-section";
				break;
			case "cardio_support":
				className = "tiss-sections-blue-section";
				break;
			case "basic_activities":
				className = "tiss-sections-purple-section";
				break;
			default:
				className = "stack-item-container";
		}
		return className;
	};

	const subcategoryColors: { [key: string]: string } = {
		ventilatory_support: variables.primary800,
		specific_interventions: variables.blue400,
		renal_support: variables.rose500,
		neuro_support: variables.yellow500,
		metabol_support: variables.green500,
		cardio_support: variables.blue600,
		basic_activities: variables.primary800,
	};

	const getSubcategoryColor = (subcategory: string): string =>
		subcategoryColors[subcategory] || variables.primary800;

	//Lógica para seleccionar botones y activar y desactivar el guardado:

	useEffect(() => {
		setCategoryList((prev: any) =>
			prev.map((item: any) => ({
				...item,
				checked: categoryState.selectedOptionsCategory.includes(item.id),
			})),
		);
	}, [categoryState.selectedOptionsCategory]);

	useEffect(() => {
		setIsDisabled(categoryState.selectedOptionsCategory.length === 0);
	}, [categoryState.selectedOptionsCategory]);

	//Lógica para el navegado con teclado

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!tissInformation) return;

			const length = Array.from(tissInformation).length;

			if (e.key === "Tab") {
				e.preventDefault();
				setActiveAccordionIndex((prev) => {
					const nextIndex = e.shiftKey
						? prev === null
							? length - 1
							: (prev - 1 + length) % length
						: prev === null
						? 0
						: (prev + 1) % length;
					setFocusedIndex(null);
					accordionRefs.current[nextIndex]?.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
					return nextIndex;
				});
			}

			if (activeAccordionIndex !== null) {
				const currentButtons = buttonRefs.current[activeAccordionIndex] || [];

				if (e.key === "ArrowDown") {
					e.preventDefault();
					if (focusedIndex === null) {
						setFocusedIndex(0);
					} else if (focusedIndex < currentButtons.length - 1) {
						setFocusedIndex((prev) => prev! + 1);
					} else if (activeAccordionIndex < length - 1) {
						const nextIndex = activeAccordionIndex + 1;
						const nextButtons = buttonRefs.current[nextIndex] || [];
						setActiveAccordionIndex(nextIndex);
						setFocusedIndex(nextButtons.length ? 0 : null);
						accordionRefs.current[nextIndex]?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				} else if (e.key === "ArrowUp") {
					e.preventDefault();
					if (focusedIndex === null) {
						setFocusedIndex(currentButtons.length - 1);
					} else if (focusedIndex > 0) {
						setFocusedIndex((prev) => prev! - 1);
					} else if (activeAccordionIndex > 0) {
						const prevIndex = activeAccordionIndex - 1;
						const prevButtons = buttonRefs.current[prevIndex] || [];
						setActiveAccordionIndex(prevIndex);
						setFocusedIndex(prevButtons.length ? prevButtons.length - 1 : null);
						accordionRefs.current[prevIndex]?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				} else if (e.key === "Enter") {
					e.preventDefault();
					if (focusedIndex !== null) {
						currentButtons[focusedIndex]?.click();
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [activeAccordionIndex, focusedIndex, tissInformation]);

	useEffect(() => {
		if (
			activeAccordionIndex !== null &&
			focusedIndex !== null &&
			buttonRefs.current[activeAccordionIndex]?.[focusedIndex]
		) {
			buttonRefs.current[activeAccordionIndex][focusedIndex]?.focus();
		}
	}, [focusedIndex, activeAccordionIndex]);

	//Función que renderiza las opciones

	const buildOptions = (): JSX.Element[] => {
		if (!tissInformation) return [];

		return Array.from(tissInformation).map(
			(value: [string, OptionCategory[]], accordionIndex) => {
				buttonRefs.current[accordionIndex] = [];

				return (
					<Box
						ref={(el) => {
							accordionRefs.current[accordionIndex] = el as HTMLDivElement | null;
						}}
					>
						<Accordion
							className="tiss-sections-section-content"
							defaultExpanded
						>
							<AccordionSummary
								className="tiss-sections-section-content-section-title"
								sx={{
									backgroundColor: getSubcategoryColor(value[1][0].subcategory),
								}}
								expandIcon={
									<ChevronDown className="tiss-sections-section-content-section-title-expand-icon" />
								}
								key={accordionIndex}
							>
								<SubcategoryIcon subcategory={value[1][0].subcategory} />
								<Typography className="tiss-sections-section-content-section-title-title">
									{value[1][0].title}
								</Typography>
							</AccordionSummary>
							<AccordionDetails className="tiss-sections-section-content-tiss-content">
								<Stack spacing={1}>
									{value[1].map((value: OptionCategory, index: number) => {
										const colorClass = SubcategoryClassName({
											subcategory: value.subcategory,
										});
										const isSelected =
											categoryState.selectedOptionsCategory.includes(
												value.id,
											);
										const isFocused =
											focusedIndex === index &&
											activeAccordionIndex === accordionIndex;
										return (
											<Button
												key={value.id}
												tabIndex={isFocused ? 0 : -1}
												ref={(el) =>
													(buttonRefs.current[accordionIndex][index] = el)
												}
												className={
													isSelected
														? `${colorClass}-selected`
														: colorClass
												}
												onClick={(e) => {
													if (activeAccordionIndex !== accordionIndex) {
														setActiveAccordionIndex(accordionIndex);
													}

													setFocusedIndex(index);

													dispatchCategory({
														type: "TOGGLE_OPTION",
														payload: {
															id: value.id,
															value: value.value,
														},
													});

													e.currentTarget.blur();
												}}
												disableRipple
											>
												<Loader
													className={
														isSelected
															? `${colorClass}-icon-selected`
															: `${colorClass}-icon`
													}
												/>
												<Typography className="stack-item-container-text">
													{value.description}
												</Typography>
											</Button>
										);
									})}
								</Stack>
							</AccordionDetails>
						</Accordion>
					</Box>
				);
			},
		);
	};

	//Lógica para obtener la info de guardado y guardar

	const getTissId = (): string => {
		return categoryState.count >= tissRange[0].min && categoryState.count <= tissRange[0].max
			? tissRange[0].id
			: tissRange[1].id;
	};

	const handleNext = (): void => {
		setSelectedPatient({
			...selectedPatient,
			categoryid: getTissId(),
			categoryname: "TISS " + categoryState.count,
			tiss_value: categoryState.count,
		});

		handleTab();
	};

	return (
		<Box>
			{isLoading ? (
				<Box>
					<Box className="category-loader-container">
						<Box className="category-loader-container__loader"></Box>
					</Box>
				</Box>
			) : (
				<Box>
					<Box className="modal-tab-content__patient-info">
						<Box sx={{ display: "flex" }}>
							<Typography className="modal-tab-content__patient-info__tag">
								Paciente:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								<User className="modal-tab-content__patient-info__info-user-icon" />
								{selectedPatient?.name}
							</Typography>
							<Typography
								sx={{ marginLeft: "30px" }}
								className="modal-tab-content__patient-info__tag"
							>
								Cama:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								{selectedPatient?.room}
							</Typography>
						</Box>
						<Box sx={{ display: "flex" }}>
							<Typography className="modal-tab-content__patient-info__tag">
								Puntaje TISS:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								{categoryState.count}
							</Typography>
						</Box>
					</Box>
					<Box className="tiss-sections">{buildOptions()}</Box>
					<Box className="modal-footer">
						<Button
							variant="outlined"
							className="modal-footer-button"
							onClick={(e) => {
								handleNext(), e.currentTarget.blur();
							}}
							disableRipple
							disabled={isDisabled}
						>
							<Typography className="modal-footer-button-label">Siguiente</Typography>
							<ArrowRight className="modal-footer-button-icon" />
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
}
